// src/composables/useCalendarEvents.js
import { ref } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';

// Define the Base API URL from environment variables
const API_BASE = process.env.VUE_APP_API_BASE_URL || '';

export function useCalendarEvents() {
  const events = ref([]);
  const loading = ref(false);
  const error = ref(null);

  const { getAccessTokenSilently } = useAuth0();

  // Helper function to build headers with Auth0 Access Token
  const getAuthHeaders = async () => {
    const token = await getAccessTokenSilently();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  // 1. GET /api/events
  const fetchEvents = async () => {
    loading.value = true;
    error.value = null;

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/events`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}:${response.statusText}`);
      }

      events.value = await response.json();
    } catch (err) {
      console.error('Failed to fetch events:', err);
      error.value = err.message || 'Failed to load events';
    } finally {
      loading.value = false;
    }
  };

  // 2. POST /api/events
  const addEvent = async (eventData) => {
    loading.value = true;
    error.value = null;

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/events`, {
        method: 'POST',
        headers,
        body: JSON.stringify(eventData),
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}:${response.statusText}`);
      }

      const createdEvent = await response.json();
      
      // Update local reactive list immediately
      events.value.push(createdEvent);
      return createdEvent;
    } catch (err) {
      console.error('Failed to create event:', err);
      error.value = err.message || 'Failed to save event';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    events,
    loading,
    error,
    fetchEvents,
    addEvent,
  };
}